import * as THREE from 'three';
import { assetReady } from './assetEvents';
import { ResourcePool } from './ResourcePool';
import type { TextureVariants } from './TextureVariants';
import { t } from '../../i18n/I18n';

export interface TextureSampler {
  repeat: [number, number];
  offset?: [number, number];
  rotation?: number;
  colorSpace: string;
  flipY: boolean;
  anisotropy: number;
}
interface ImageRecord {
  texture?: THREE.Texture; templates: Set<THREE.Texture>; variants: Set<THREE.Texture>;
  /** The last request failed and no other is queued: the samplers draw the stand-in. */
  failed: boolean;
  /** Queues a request for the picture, the first one or again after a failure; every sampler of the record follows it. */
  load: () => void;
}
export interface TextureCacheOptions { maxConcurrent?: number; maxPendingBytes?: number; estimateBytes?: (url: string) => number; now?: () => number }

/**
 * How long a picture rests after failing `count` times in a row before a use asks for it again:
 * 30 s, doubling up to 10 minutes. Every build of the scene asks for its pictures, and the shell is
 * built again each time the catalog answers while a project opens, so without a pause a missing
 * picture was asked for some 20 times in the first seconds and again on every edit.
 */
export function retryPause(count: number): number { return Math.min(30_000 * 2 ** (count - 1), 10 * 60_000); }

let whitePixel: ImageData | undefined, flatNormalPixel: ImageData | undefined;
/**
 * What a sampler draws while its picture is on its way, and for good if it fails to load: one
 * opaque pixel that leaves the material as it looks without the picture, as the app draws a
 * material whose picture is missing. White keeps a colour map's material colour; a normal map (the
 * cache decodes those with no colour space) gets the flat normal, as white would tilt the surface.
 * Without it three.js samples an empty texture as black and warns on every frame. None outside a
 * browser.
 */
function standInImage(colorSpace: string): ImageData | undefined {
  if (typeof ImageData === 'undefined') return undefined;
  return colorSpace === THREE.NoColorSpace
    ? flatNormalPixel ??= new ImageData(new Uint8ClampedArray([128, 128, 255, 255]), 1, 1)
    : whitePixel ??= new ImageData(new Uint8ClampedArray([255, 255, 255, 255]), 1, 1);
}

/** Whether a sampler draws its picture, not the stand-in of one still on its way or failed. */
export function hasPicture(texture: THREE.Texture): boolean {
  const image = texture.image;
  return !!image && image !== whitePixel && image !== flatNormalPixel;
}

/** A decoded image/Source is independent of UV sampling. Samplers share that Source,
 * so scale/rotation never re-request an already-ready image or upload it again. */
export class TextureImageCache {
  private images = new Map<string, ImageRecord>();
  private samplers = new Map<string, THREE.Texture>();
  /** Pictures whose last request failed, by URL: failures in a row and when a use may ask again. */
  private failures = new Map<string, { count: number; retryAt: number }>();
  private entries = new Set<object>();
  private disposed = false;
  private queue: Array<{ bytes: number; run: () => void }> = [];
  private active = 0;
  private pendingBytes = 0;
  private variants?: Pick<TextureVariants, 'load'>;
  failed = 0;
  get size(): number { return this.samplers.size; }

  constructor(private loader = new THREE.TextureLoader(), readonly resources = new ResourcePool(), private options: TextureCacheOptions = {}) { loader.crossOrigin = 'anonymous'; }
  setVariants(variants: Pick<TextureVariants, 'load'>): void { this.variants = variants; }

  get(url: string, options: TextureSampler): THREE.Texture {
    if (this.disposed) throw new Error(t('#ViewLoadCanceled'));
    const offset = options.offset ?? [0, 0], rotation = options.rotation ?? 0;
    const key = JSON.stringify([url, options.repeat, offset, rotation, options.colorSpace, options.flipY, options.anisotropy]);
    const cached = this.samplers.get(key);
    if (cached && !this.failures.has(url)) return cached;
    // Color and normal decoding have separate semantics, matching native FloorTextures.
    const imageKey = JSON.stringify([url, options.colorSpace, !!this.variants && options.flipY]);
    let record = this.images.get(imageKey);
    // A picture that failed rests: the builds meanwhile draw its stand-in without asking, and the
    // first use after the pause asks again, for every sampler of it at once.
    if (record?.failed && this.due(url)) record.load();
    if (cached) return cached;
    record ??= this.record(url, imageKey, options);
    const texture = this.configure(record.texture!, options);
    texture.userData.imageUrl = url;
    record.variants.add(texture);
    this.samplers.set(key, texture);
    this.trackSampler(texture, key, record);
    return texture;
  }

  /**
   * Ends the pause of every picture that failed: the browser is back online, or the person asked
   * for the scene again. A picture something draws is asked for at once, once, and the surfaces
   * drawing its stand-in show it when it lands; any other is asked for by its next use.
   */
  retryFailed(): void {
    if (this.disposed) return;
    for (const failure of this.failures.values()) failure.retryAt = 0;
    for (const record of this.images.values()) if (record.failed && this.resources.retained(record)) record.load();
  }

  /** The record of one decoded picture, asking for it unless the picture failed and still rests. */
  private record(url: string, imageKey: string, options: TextureSampler): ImageRecord {
    // Until the picture is in, the samplers draw the stand-in from the template's Source; the
    // picture comes in a Source of its own (see `ready`).
    const template = new THREE.Texture(standInImage(options.colorSpace));
    const entry: ImageRecord = { texture: template, templates: new Set([template]), variants: new Set(), failed: false, load: () => {} };
    this.images.set(imageKey, entry);
    this.entries.add(entry);
    const forget = () => {
      this.entries.delete(entry);
      if (this.images.get(imageKey) === entry) this.images.delete(imageKey);
      for (const texture of [...entry.variants]) this.resources.remove(texture);
    };
    const bytes = Math.max(1, this.options.estimateBytes?.(url) ?? 16 * 1024 * 1024);
    // The stand-in's Source stays the entry's for good: a lease taken while it was drawn pins the entry through it.
    this.resources.register(entry, [template, template.source], forget);
    entry.load = () => {
      entry.failed = false;
      this.resources.register(entry, [], forget, -1);
      this.queue.push({ bytes, run: () => {
        this.resources.register(entry, [], forget, bytes);
        let finished = false;
        const finish = () => {
          if (finished) return;
          finished = true; this.active--; this.pendingBytes -= bytes; this.drain();
        };
        const ready = (loaded: THREE.Texture) => {
          if (this.disposed || !this.entries.has(entry)) {
            this.resources.disposeLoose([loaded, loaded.source]); this.resources.closeImage(loaded.image); finish(); return;
          }
          if (finished) return;
          this.failures.delete(url);
          entry.texture = loaded; entry.templates.add(loaded);
          this.resources.register(entry, [loaded, loaded.source], forget);
          // The samplers move from the stand-in to the picture's Source. three.js allocates a texture's
          // GPU storage at the size of its first upload, so their upload of the pixel is released
          // first, or the picture would be written into its 1×1 storage (a GL error, the surface stuck
          // white); the picture gets storage of its own and is uploaded once. A copy of a sampler the
          // cache does not know keeps the pixel.
          for (const texture of entry.variants) { texture.dispose(); texture.source = loaded.source; texture.needsUpdate = true; }
          finish(); assetReady();
          if (this.variants) void this.upgrade(entry, url, options.flipY, forget);
          queueMicrotask(() => this.resources.trim());
        };
        const failed = () => {
          if (finished) return;
          // The samplers keep the stand-in they have drawn since they were handed out, and the
          // record keeps the samplers: the next request, after the pause, is for all of them.
          entry.failed = true; this.failed++;
          if (!this.disposed) { this.rest(url); this.resources.register(entry, [], forget); }
          finish(); queueMicrotask(() => this.resources.trim());
        };
        try {
          const loaded = this.loader.load(url, ready, undefined, failed);
          if (!finished) {
            // The loader's texture keeps its own Source, empty until the picture is in.
            this.resources.register(entry, [loaded, loaded.source], forget, bytes);
            if (loaded.image) ready(loaded);
          }
        } catch { failed(); }
      } });
      this.drain();
    };
    if (this.due(url)) entry.load(); else entry.failed = true;
    return entry;
  }

  /** Starts the pause of a picture that failed. A request that was already on its way (another decoding of the picture) does not lengthen it. */
  private rest(url: string): void {
    const now = this.now(), failure = this.failures.get(url);
    if (failure && now < failure.retryAt) return;
    const count = (failure?.count ?? 0) + 1;
    this.failures.set(url, { count, retryAt: now + retryPause(count) });
  }

  private due(url: string): boolean {
    const failure = this.failures.get(url);
    return !failure || this.now() >= failure.retryAt;
  }

  private now(): number { return this.options.now?.() ?? performance.now(); }

  /** Embedded GLB images already have a decoded Source and do not need image I/O. */
  fromTexture(source: THREE.Texture, options: TextureSampler): THREE.Texture {
    if (this.disposed) throw new Error(t('#ViewLoadCanceled'));
    const key = JSON.stringify(['embedded', source.uuid, options]);
    let texture = this.samplers.get(key);
    if (!texture) { texture = this.configure(source, options); this.samplers.set(key, texture); this.trackSampler(texture, key); }
    return texture;
  }

  private configure(source: THREE.Texture, options: TextureSampler): THREE.Texture {
    const texture = source.clone(), offset = options.offset ?? [0, 0], rotation = options.rotation ?? 0;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(...options.repeat); texture.offset.set(offset[0], offset[1]);
    // Three's setUvTransform uses R(-angle), whereas Unity rotates UV by +degrees.
    texture.center.set(0, 0); texture.rotation = -rotation * Math.PI / 180;
    texture.colorSpace = options.colorSpace; texture.flipY = 'isCompressedTexture' in source ? false : options.flipY; texture.anisotropy = options.anisotropy;
    if ('isCompressedTexture' in source) Object.assign(texture, { isCompressedTexture: true });
    return texture;
  }

  private async upgrade(entry: ImageRecord, url: string, flipY: boolean, forget: () => void): Promise<void> {
    const texture = await this.variants!.load(url, flipY);
    if (!texture) return;
    if (this.disposed || !this.entries.has(entry)) { this.resources.disposeLoose([texture, texture.source]); return; }
    const previous = entry.texture!.source;
    this.resources.register(entry, [texture, texture.source], forget);
    for (const target of [...entry.templates, ...entry.variants]) {
      // The renderer keys GPU state by Source: release the legacy upload while the sampler still
      // names it, or that entry leaks and a later dispose reads a stale key under the new Source.
      target.dispose();
      target.source = texture.source; target.mipmaps = texture.mipmaps; target.format = texture.format; target.type = texture.type;
      target.internalFormat = texture.internalFormat; target.generateMipmaps = false; target.flipY = false;
      target.userData.performanceVariant = texture.userData.performanceVariant;
      Object.assign(target, { isCompressedTexture: true }); target.needsUpdate = true;
    }
    this.resources.replaceSource(entry, previous, texture.source);
    this.resources.trim();
    assetReady();
  }

  private trackSampler(texture: THREE.Texture, key: string, record?: ImageRecord): void {
    this.entries.add(texture);
    this.resources.register(texture, [texture], () => {
      this.entries.delete(texture); record?.variants.delete(texture);
      if (this.samplers.get(key) === texture) this.samplers.delete(key);
    });
  }

  private drain(): void {
    if (this.disposed) return;
    const concurrent = Math.max(1, this.options.maxConcurrent ?? 2), budget = this.options.maxPendingBytes ?? 32 * 1024 * 1024;
    while (this.queue.length && this.active < concurrent) {
      const next = this.queue[0];
      if (this.active && this.pendingBytes + next.bytes > budget) return;
      this.queue.shift(); this.active++; this.pendingBytes += next.bytes; next.run();
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.queue = [];
    for (const key of [...this.entries]) this.resources.remove(key);
    this.entries.clear(); this.images.clear(); this.samplers.clear(); this.failures.clear();
  }
}
